'use client';

import { useEffect, useState } from 'react';
import { FiCreditCard, FiCheckCircle, FiXCircle, FiClock, FiAlertCircle } from 'react-icons/fi';

interface User {
  id: string;
  name: string;
  email: string;
}

type PaymentStatus = 'pending' | 'completed' | 'failed' | 'cancelled' | 'captured' | 'created';

interface PaymentItem {
  id: string;
  order_id: string;
  amount: number;
  status: PaymentStatus;
  transaction_id?: string;
  created_at: string;
  updated_at: string;
  plan?: {
    name: string;
    price: number;
    currency: string;
  };
}

interface PaymentsResponse {
  success: boolean;
  payments: PaymentItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface PaymentsHistoryProps {
  user: User;
}

export default function PaymentsHistory({ user }: PaymentsHistoryProps) {
  const [payments, setPayments] = useState<PaymentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch('/api/payment/history?limit=50', {
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to load payments');
      }
      const data: PaymentsResponse = await res.json();
      if (data.success) {
        // Only show successful payments to users
        const successfulPayments = (data.payments || []).filter(p => 
          p.status === 'completed' || p.status === 'captured'
        );
        setPayments(successfulPayments);
      } else {
        throw new Error('Failed to load payments');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initial load; effect performs async work
    loadPayments();
  }, [user?.id]);

  const refreshUnverifiedOrders = async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      // Fetch all payments to find pending ones (including non-successful ones)
      const res = await fetch('/api/payment/history?limit=100', {
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to load payments for refresh');
      }
      const data: PaymentsResponse = await res.json();
      const allPayments = data.success ? (data.payments || []) : [];
      
      // Find pending payments that need refresh
      const pendingPayments = allPayments.filter(p => 
        p.status === 'pending' || p.status === 'failed' || p.status === 'created'
      );
      
      if (pendingPayments.length === 0) {
        // No pending payments to refresh
        await loadPayments(); // Just reload to get latest data
        return;
      }

      // For each pending payment, call the manual verify endpoint to update status
      const updatePromises = pendingPayments.map(async (payment) => {
        try {
          const response = await fetch('/api/payment/manual-verify', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              orderId: payment.order_id,
              fetchFromRazorpay: true, // This will fetch latest status from Razorpay
            }),
          });
          
          if (response.ok) {
            console.log(`Updated payment ${payment.order_id}`);
          } else {
            console.log(`Failed to update payment ${payment.order_id}`);
          }
        } catch (error) {
          console.error(`Error updating payment ${payment.order_id}:`, error);
        }
      });

      // Wait for all updates to complete
      await Promise.all(updatePromises);

      // Reload payments list to show updated statuses
      await loadPayments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderStatus = (status: PaymentStatus) => {
    switch (status) {
      case 'completed':
      case 'captured':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
            <FiCheckCircle size={12} /> Completed
          </span>
        );
      case 'pending':
      case 'created':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">
            <FiClock size={12} /> Pending
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
            <FiXCircle size={12} /> Failed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-700">
            <FiAlertCircle size={12} /> Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
            <FiAlertCircle size={12} /> {status}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Payments</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
                     <div className="text-slate-600 text-sm">
             Showing {payments.length} successful payment{payments.length !== 1 ? 's' : ''}
           </div>
          <button
            onClick={refreshUnverifiedOrders}
            disabled={refreshing}
            className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors disabled:opacity-50 text-sm"
          >
                         {refreshing ? 'Refreshing...' : 'Check for Updates'}
          </button>
        </div>
        {loading ? (
          <div className="text-center">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-slate-600 text-sm">Loading payments...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        ) : payments.length === 0 ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <FiCreditCard className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-base font-semibold text-slate-900 mb-1">No payments found</h3>
            <p className="text-slate-600 text-sm">You haven't made any payments yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {payments.map((payment) => (
              <div key={payment.id} className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center text-slate-600">
                      <FiCreditCard size={16} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900 text-sm">
                          {payment.plan?.name || 'Payment'}
                        </h3>
                        {renderStatus(payment.status)}
                      </div>
                      <p className="text-xs text-slate-500">{formatDate(payment.created_at)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-900">
                      ₹{payment.amount.toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-500">{payment.plan?.currency || 'INR'}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 text-xs">
                  <div className="text-slate-600">
                    <span className="text-slate-500">Order ID: </span>
                    <span className="font-mono">{payment.order_id}</span>
                  </div>
                  {payment.transaction_id && (
                    <div className="text-slate-600">
                      <span className="text-slate-500">Transaction ID: </span>
                      <span className="font-mono">{payment.transaction_id}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


